/* eslint-disable jsx-a11y/label-has-associated-control */
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';

type SlotForm = {
  quantity: string;
};

interface SlotFormErrors {
  quantity?: string;
}

interface PurchaseSlotModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onSubmit: (data: { id: string; quantity: number }) => Promise<void>;
  isLoading: boolean;
  id: string;
  slot?: any;
}

export default function PurchaseSlotModal({
  isOpen,
  setIsOpen,
  onSubmit,
  isLoading,
  id,
  slot,
}: PurchaseSlotModalProps) {
  const [errors, setErrors] = useState<SlotFormErrors>({});
  const [form, setForm] = useState<SlotForm>({ quantity: '' });

  const totalCost =
    form.quantity && slot ? slot.slotPrice * Number(form.quantity) : null;

  const validate = (): boolean => {
    const newErrors: SlotFormErrors = {};
    if (!form.quantity || Number(form.quantity) < 1)
      newErrors.quantity = 'Enter a valid quantity';
    if (slot && Number(form.quantity) > slot.availableSlots)
      newErrors.quantity = `Only ${slot.availableSlots} slot(s) available`;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const closeModal = () => {
    setForm({ quantity: '' });
    setErrors({});
    setIsOpen(false);
  };

  const handleOk = async () => {
    if (!validate()) return;
    try {
      await onSubmit({ id, quantity: Number(form.quantity) });
      closeModal();
    } catch {
      // error toast is shown by the parent component
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-10' onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black bg-opacity-25' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex items-center justify-center min-h-full p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl'>
                <Dialog.Title
                  as='h3'
                  className='text-lg font-medium leading-6 text-gray-900'
                >
                  Purchase Slot — {slot?.name}
                </Dialog.Title>

                {slot && (
                  <div className='mt-3 flex flex-wrap gap-3 text-sm text-gray-600'>
                    <span className='bg-gray-100 px-2 py-1 rounded'>
                      {slot.sector}
                    </span>
                    <span
                      className={`px-2 py-1 rounded font-medium ${
                        slot.riskLevel === 'Low'
                          ? 'bg-green-100 text-green-700'
                          : slot.riskLevel === 'Medium'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {slot.riskLevel} Risk
                    </span>
                    <span className='bg-blue-50 text-blue-700 px-2 py-1 rounded'>
                      ₦{slot.slotPrice?.toLocaleString()} / slot
                    </span>
                    <span className='text-gray-500'>
                      {slot.availableSlots} slot(s) left
                    </span>
                  </div>
                )}

                <div className='mt-4 space-y-4'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Number of Slots
                    </label>
                    <input
                      type='number'
                      min='1'
                      max={slot?.availableSlots}
                      value={form.quantity}
                      onChange={(e) => setForm({ quantity: e.target.value })}
                      className='w-full border rounded-md p-2 shadow-all-sides'
                    />
                    {errors.quantity && (
                      <p className='text-xs text-red-500 mt-1'>
                        {errors.quantity}
                      </p>
                    )}
                  </div>

                  {totalCost !== null && (
                    <div className='bg-blue-50 rounded-lg p-4 text-sm space-y-1'>
                      <h4 className='font-semibold text-gray-900'>Summary</h4>
                      <div className='flex justify-between'>
                        <span className='text-gray-600'>Slots:</span>
                        <span className='font-medium'>{form.quantity}</span>
                      </div>
                      <div className='flex justify-between'>
                        <span className='text-gray-600'>Price per slot:</span>
                        <span className='font-medium'>
                          ₦{slot.slotPrice?.toLocaleString()}
                        </span>
                      </div>
                      <div className='flex justify-between border-t pt-1 mt-1'>
                        <span className='text-gray-700 font-semibold'>
                          Total:
                        </span>
                        <span className='font-bold text-blue-700'>
                          ₦{totalCost.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className='flex flex-row justify-center mt-6 gap-2'>
                  <button
                    type='button'
                    className='px-4 py-2 text-sm font-medium text-black rounded-md bg-slate-200'
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    disabled={isLoading}
                    className='px-4 py-2 text-sm font-medium text-white rounded-md bg-green-600 disabled:opacity-60'
                    onClick={handleOk}
                  >
                    {isLoading ? 'Processing...' : 'Confirm Purchase'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
